import React, {useEffect, useReducer} from 'react';
import PropTypes from 'prop-types';
import {isPlainObject} from 'lodash/lang';

const getSetRecordsState = ({records, currentPage, pageCount, totalCount, itemsPerPage}, prevState) => {
  const sortedIds = [];
  const newRecordPage = {};
  for (let record of records) {
    sortedIds.push(record.id);
    newRecordPage[record.id] = record;
  }
  newRecordPage['sortedIds'] = sortedIds;
  return {
    ...prevState,
    recordPages: {
      ...prevState.recordPages,
      [currentPage]: newRecordPage,
      currentPage,
      totalCount,
      pageCount,
      itemsPerPage,
    },
    initialized: true,
  };
};

const getClearedRecordsAfterPageState = (page, prevState) => {
  page = parseInt(page) || 0;
  const pageNumbers = Object.keys(prevState.recordPages).filter((v) => {
    const parsed = parseInt(v);
    return !isNaN(parsed) && parsed <= page;
  });
  const recordPages = pageNumbers.reduce(
    (obj, num) => ({
      ...obj,
      [num]: {
        ...prevState.recordPages[num],
      },
    }),
    {},
  );
  return {
    ...prevState,
    recordPages: {
      ...recordPages,
      currentPage: page || 1,
      totalCount: prevState.recordPages.totalCount,
      pageCount: prevState.recordPages.pageCount,
      itemsPerPage: prevState.recordPages.itemsPerPage,
    },
  };
};

const getRefreshedTableState = (state, fetchRecords, hardRefresh, signal) => {
  const {page: currentPage, itemsPerPage, filterValue, sortOpts, extraFilters} = state;
  const page = hardRefresh ? 1 : currentPage;
  return fetchRecords(
    {
      q: filterValue,
      page,
      perPage: itemsPerPage,
      sortOpts,
      extraParams: extraFilters,
    },
    signal,
  ).then(({records, recordCount, pageCount}) => {
    const newState = getSetRecordsState(
      {records, currentPage: page, pageCount, totalCount: recordCount, itemsPerPage},
      state,
    );
    return getClearedRecordsAfterPageState(page, newState);
  });
};

export const useListFacilities = ({
  fetchRecords,
  deleteFn,
  formatErrors,
  addFlashMessage,
  deletionSuccessMsg = 'The record has been successfully deleted!',
  handleDeletionSuccess,
}) => {
  const [state, dispatch] = useReducer(
    (state, action) => {
      switch (action.type) {
        case 'OPEN_MODAL':
          return {
            ...state,
            modalOpen: true,
            currentRecord: action.currentRecord || null,
          };
        case 'CLOSE_MODAL':
          return {
            ...state,
            modalOpen: false,
            currentRecord: null,
          };
        case 'HANDLE_ACTION_SUCCESS':
          return {
            ...state,
            modalOpen: false,
            currentRecord: null,
            hardRefreshKey: state.hardRefreshKey + 1,
          };
        case 'TRIGGER_SOFT_REFRESH':
          return {
            ...state,
            modalOpen: false,
            currentRecord: null,
            softRefreshKey: state.softRefreshKey + 1,
          };
        case 'REFRESH_TABLE':
          return {
            ...state,
            tableState: action.tableState,
          };
        case 'SET_CURRENT_RECORD_PAGE':
          return {
            ...state,
            tableState: {
              ...state.tableState,
              recordPages: {
                ...state.tableState.recordPages,
                currentPage: action.currentPage,
              },
            },
          };
        case 'SELECT_PAGE':
          return {
            ...state,
            tableState: {
              ...state.tableState,
              page: action.page,
            },
          };
        case 'SELECT_ITEMS_PER_PAGE':
          return {
            ...state,
            tableState: {
              ...state.tableState,
              itemsPerPage: action.itemsPerPage,
            },
          };
        case 'CHANGE_FILTER':
          return {
            ...state,
            tableState: {
              ...state.tableState,
              filterValue: action.filterValue,
            },
          };
        case 'CHANGE_SORTER':
          return {
            ...state,
            tableState: {
              ...state.tableState,
              sortOpts: action.sortOpts,
            },
          };
        case 'CHANGE_EXTRA_FILTERS':
          return {
            ...state,
            tableState: {
              ...state.tableState,
              extraFilters: {
                ...state.tableState.extraFilters,
                ...action.extraFilters,
              },
            },
          };
        case 'SET_CURRENT_RECORD':
          return {
            ...state,
            currentRecord: action.currentRecord,
          };
        default:
          return state;
      }
    },
    {
      modalOpen: false,
      currentRecord: null,
      hardRefreshKey: 0,
      softRefreshKey: 0,
      tableState: {
        recordPages: {
          currentPage: 1,
          totalCount: 0,
          pageCount: 1,
          itemsPerPage: 0,
        },
        page: 1,
        itemsPerPage: 50,
        filterValue: '',
        sortOpts: {},
        extraFilters: {},
        initialized: false,
      },
    },
  );

  const openModal = (currentRecord) => dispatch({type: 'OPEN_MODAL', currentRecord});
  const closeModal = () => dispatch({type: 'CLOSE_MODAL'});
  const handleActionSuccess = () => dispatch({type: 'HANDLE_ACTION_SUCCESS'});
  const triggerSoftRefresh = () => dispatch({type: 'TRIGGER_SOFT_REFRESH'});

  const setCurrentRecord = (currentRecord) => dispatch({type: 'SET_CURRENT_RECORD', currentRecord});
  const refreshTable = (hardRefresh = false, signal) =>
    getRefreshedTableState(state.tableState, fetchRecords, hardRefresh, signal).then((tableState) =>
      dispatch({type: 'REFRESH_TABLE', tableState}),
    );
  const setCurrentRecordPage = (currentPage) => dispatch({type: 'SET_CURRENT_RECORD_PAGE', currentPage});
  const selectPage = (page) => dispatch({type: 'SELECT_PAGE', page});
  const selectItemsPerPage = (itemsPerPage) => dispatch({type: 'SELECT_ITEMS_PER_PAGE', itemsPerPage});
  const changeFilter = (filterValue) => dispatch({type: 'CHANGE_FILTER', filterValue});
  const changeSorter = (fieldName, sortDir) => {
    let sortOpts = {};
    if (sortDir) {
      sortOpts = {
        sortField: fieldName,
        sortDir,
      };
    }
    return dispatch({type: 'CHANGE_SORTER', sortOpts});
  };
  const changeExtraFilters = (e) => {
    let extraFilters;
    if (e.hasOwnProperty('name') && e.hasOwnProperty('value')) {
      extraFilters = {
        [e.name]: e.value,
      };
    } else if (isPlainObject(e)) {
      extraFilters = e;
    } else {
      extraFilters = {
        [e.target.name]: e.target.value,
      };
    }
    return dispatch({type: 'CHANGE_EXTRA_FILTERS', extraFilters});
  };
  const setNextPageRecordsAndCurrentRecord = (page, increase, state) => {
    const newTableState = {
      ...state,
      recordPages: {
        ...state.recordPages,
        currentPage: page,
      },
      page,
    };
    return getRefreshedTableState(newTableState, fetchRecords)
      .then((tableState) => {
        dispatch({type: 'REFRESH_TABLE', tableState});
        return tableState;
      })
      .then((tableState) => {
        const recordPage = tableState.recordPages[page];
        const sortedIds = recordPage['sortedIds'];
        const recordId = increase === 1 ? sortedIds[0] : sortedIds[sortedIds.length - 1];
        const currentRecord = recordPage[recordId];
        setCurrentRecord(currentRecord);
      });
  };

  const {
    hardRefreshKey,
    softRefreshKey,
    tableState: {page, recordPages, itemsPerPage, filterValue, sortOpts, extraFilters, initialized},
  } = state;

  useEffect(() => {
    const controller = new AbortController();
    refreshTable(true, controller.signal);
    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (!initialized) return;
    if (recordPages.hasOwnProperty(page)) {
      setCurrentRecordPage(page);
    } else {
      refreshTable();
    }
  }, [page, initialized]);

  useEffect(() => {
    if (!initialized) return;
    const controller = new AbortController();
    refreshTable(true, controller.signal);
    return () => {
      controller.abort();
    };
  }, [itemsPerPage, filterValue, sortOpts, extraFilters, hardRefreshKey]);

  useEffect(() => {
    if (!initialized) return;
    refreshTable();
  }, [softRefreshKey]);

  const deleteRecord = !deleteFn
    ? undefined
    : (record) => {
        deleteFn(record.id)
          .then(() => {
            handleActionSuccess();
            addFlashMessage(deletionSuccessMsg, 'success');
            handleDeletionSuccess && handleDeletionSuccess();
            return Promise.resolve();
          })
          .catch((err) => formatErrors(err));
      };

  return {
    ...state,
    refreshTable,
    selectPage,
    selectItemsPerPage,
    changeFilter,
    changeSorter,
    changeExtraFilters,
    openModal,
    closeModal,
    handleActionSuccess,
    triggerSoftRefresh,
    setCurrentRecord,
    setNextPageRecordsAndCurrentRecord,
    deleteRecord,
  };
};

export const SimpleList = ({Component, fetchRecords, ...restProps}) => {
  const {
    tableState,
    refreshTable,
    selectPage,
    selectItemsPerPage,
    changeFilter,
    changeSorter,
    changeExtraFilters,
  } = useListFacilities({
    fetchRecords,
  });
  return React.createElement(Component, {
    tableState,
    refreshTable,
    selectPage,
    selectItemsPerPage,
    changeFilter,
    changeSorter,
    changeExtraFilters,
    ...restProps,
  });
};

SimpleList.propTypes = {
  Component: PropTypes.func.isRequired,
  fetchRecords: PropTypes.func.isRequired,
};
